import React, { useEffect, useMemo, useState } from "react";

import { useToast } from "../hooks/use-toast";
import useZar8UnlockFlow from "../hooks/useZar8UnlockFlow";
import { parseNumericValue } from "../utils/numberFormatter";
import {
  buildZar8UnlockPositionOptions,
  calculateZar8UnlockSummary,
  sanitizeZar8AmountInput,
  ZAR8_LOCK_FLOW_STEPS,
  ZAR8_YIELD_FLOW_TYPES,
} from "../utils/zar8LockFlow";
import TwoFA from "./MainTwoFA";
import {
  UnlockAmountStep,
  UnlockConfirmationModal,
  UnlockProcessingStep,
  UnlockSuccessStep,
  UnlockSummaryStep,
  UnlockTwoFactorStep,
} from "./Zar8UnlockFlowSteps";
import { FlowModePill, StepShell } from "./ui/zar8-yield-flow-ui";

export default function Zar8UnlockFlow({
  flowState,
  onFlowStateChange,
  activeYieldEntries,
  zar8Asset,
  isTwoFactorEnabled,
  onClose,
  onModeChange,
}) {
  const { toast } = useToast();
  const [unlockQuote, setUnlockQuote] = useState(null);

  const unlockablePositions = useMemo(
    () => buildZar8UnlockPositionOptions(activeYieldEntries),
    [activeYieldEntries]
  );
  const selectedUnlockPosition = useMemo(() => {
    if (unlockablePositions.length === 0) {
      return null;
    }

    return (
      unlockablePositions.find(
        (position) => position.id === flowState.selectedUnlockPositionId
      ) || unlockablePositions[0]
    );
  }, [flowState.selectedUnlockPositionId, unlockablePositions]);

  const amountValue = parseNumericValue(flowState.amountInput);
  const lockedBalance = parseNumericValue(selectedUnlockPosition?.lockedAmount);
  const unlockSummary = useMemo(
    () =>
      calculateZar8UnlockSummary({
        amountZar: amountValue,
        lockedAmountZar: lockedBalance,
        feeType: selectedUnlockPosition?.feeType,
        feeAmount: selectedUnlockPosition?.feeAmount,
        quote: unlockQuote,
      }),
    [
      amountValue,
      lockedBalance,
      selectedUnlockPosition?.feeAmount,
      selectedUnlockPosition?.feeType,
      unlockQuote,
    ]
  );

  const {
    amountError,
    handleAmountContinue,
    handleAmountInputChange,
    handleBack,
    handleClose,
    handleConfirmUnlock,
    handleMaxAmount,
    handleOtpVerify,
    handlePositionSelect,
    handleSummaryContinue,
    hasAcceptedTerms,
    isConfirmationModalOpen,
    isConfirmingUnlock,
    isQuoteLoading,
    isTermPickerOpen,
    isVerifyingOtp,
    otpCode,
    otpError,
    resetForMode,
    setHasAcceptedTerms,
    setIsConfirmationModalOpen,
    setIsTermPickerOpen,
    setOtpCode,
    setOtpError,
    setShowTwoFactorSetup,
    setSummaryError,
    setTwoFactorReadyOverride,
    showTwoFactorSetup,
    summaryError,
    twoFactorReadyOverride,
  } = useZar8UnlockFlow({
    amountValue,
    flowState,
    lockedBalance,
    onClose,
    onFlowStateChange,
    onModeChange,
    selectedUnlockPosition,
    toast,
    unlockQuote,
    setUnlockQuote,
  });

  const isTwoFactorReady = isTwoFactorEnabled || twoFactorReadyOverride;
  const topAsset = zar8Asset?.assetIcon || zar8Asset?.image || "";

  useEffect(() => {
    if (unlockablePositions.length === 0) {
      return;
    }

    const hasValidSelectedPosition = unlockablePositions.some(
      (position) => position.id === flowState.selectedUnlockPositionId
    );

    if (!hasValidSelectedPosition) {
      onFlowStateChange((currentState) => ({
        ...currentState,
        selectedUnlockPositionId: unlockablePositions[0].id,
      }));
    }
  }, [
    flowState.selectedUnlockPositionId,
    onFlowStateChange,
    unlockablePositions,
  ]);

  return (
    <StepShell>
      {isConfirmationModalOpen ? (
        <UnlockConfirmationModal
          onClose={() => setIsConfirmationModalOpen(false)}
          onConfirm={handleConfirmUnlock}
          isConfirming={isConfirmingUnlock}
        />
      ) : null}

      {showTwoFactorSetup ? (
        <div className="overflow-hidden rounded-[24px] border border-[#EBE9EF] bg-white">
          <TwoFA
            closeOverlay={() => setShowTwoFactorSetup(false)}
            isSetUp={() => {
              setShowTwoFactorSetup(false);
              setTwoFactorReadyOverride(true);
            }}
            on2FAEnabled={() => setTwoFactorReadyOverride(true)}
            onDoneClick={() => {
              setShowTwoFactorSetup(false);
              setTwoFactorReadyOverride(true);
            }}
            className="rounded-none border-none shadow-none"
          />
        </div>
      ) : null}

      {!showTwoFactorSetup && flowState.step === ZAR8_LOCK_FLOW_STEPS.amount ? (
        <UnlockAmountStep
          amountError={amountError}
          amountInput={flowState.amountInput}
          lockedBalance={lockedBalance}
          modeSwitch={
            <FlowModePill
              currentMode={ZAR8_YIELD_FLOW_TYPES.unlock}
              onChange={resetForMode}
            />
          }
          onAmountInputChange={(value) =>
            handleAmountInputChange(sanitizeZar8AmountInput(value))
          }
          onBack={handleBack}
          onCancel={handleClose}
          onContinue={handleAmountContinue}
          onMaxAmount={handleMaxAmount}
          onPositionSelect={handlePositionSelect}
          onToggleTermPicker={() => {
            if (unlockablePositions.length === 0) {
              return;
            }

            setIsTermPickerOpen((currentValue) => !currentValue);
          }}
          selectedUnlockPosition={selectedUnlockPosition}
          topAsset={topAsset}
          unlockablePositions={unlockablePositions}
          isTermPickerOpen={isTermPickerOpen}
        />
      ) : null}

      {!showTwoFactorSetup &&
      flowState.step === ZAR8_LOCK_FLOW_STEPS.summary ? (
        <UnlockSummaryStep
          amountValue={amountValue}
          hasAcceptedTerms={hasAcceptedTerms}
          isQuoteLoading={isQuoteLoading}
          onBack={handleBack}
          onCancel={handleClose}
          onContinue={handleSummaryContinue}
          onToggleAcceptedTerms={() => {
            setSummaryError("");
            setHasAcceptedTerms((current) => !current);
          }}
          summaryError={summaryError}
          unlockSummary={unlockSummary}
        />
      ) : null}

      {!showTwoFactorSetup &&
      flowState.step === ZAR8_LOCK_FLOW_STEPS.twoFactor ? (
        <UnlockTwoFactorStep
          isTwoFactorReady={isTwoFactorReady}
          isVerifyingOtp={isVerifyingOtp}
          onBack={handleBack}
          onCancel={handleClose}
          onEnable2FA={() => setShowTwoFactorSetup(true)}
          onOtpCodeChange={(value) => {
            setOtpError("");
            setOtpCode(value);
          }}
          onVerify={handleOtpVerify}
          otpCode={otpCode}
          otpError={otpError}
          summaryError={summaryError}
        />
      ) : null}

      {!showTwoFactorSetup &&
      flowState.step === ZAR8_LOCK_FLOW_STEPS.processing ? (
        <UnlockProcessingStep />
      ) : null}

      {!showTwoFactorSetup &&
      flowState.step === ZAR8_LOCK_FLOW_STEPS.success ? (
        <UnlockSuccessStep onDone={onClose} />
      ) : null}
    </StepShell>
  );
}
